import { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { SectionHeading } from "./Reveal";
import { ProductCard } from "./ProductCard";
import { fmtPrice } from "@/data/site";
import { useProducts } from "@/lib/products";

const TABS = [
  { id: "all", label: "Tümü" },
  { id: "saat", label: "Saatler" },
  { id: "taki", label: "Takı" },
  { id: "gozluk", label: "Gözlük" },
];

export const SelectedProducts = ({ category, onCategoryChange, priceFilter, onClearPrice }) => {
  const { products } = useProducts();
  const items = useMemo(
    () =>
      products.filter(
        (p) =>
          (category === "all" || p.category === category) &&
          (!priceFilter || p.price <= priceFilter)
      ),
    [products, category, priceFilter]
  );

  return (
    <section id="katalog" data-testid="selected-products-section" className="py-16 md:py-24 lg:py-32 bg-ivory">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Seçkin Ürünler"
          title={<>Katalog — <span className="italic text-emerald">sezonun favorileri</span></>}
          testId="selected-products"
        />

        <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-3" data-testid="catalog-filter-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => onCategoryChange(t.id)}
              data-testid={`filter-tab-${t.id}`}
              className={`h-11 rounded-full px-6 text-[13px] font-semibold transition-colors duration-300 ${
                category === t.id
                  ? "bg-emerald text-ivory"
                  : "bg-white border border-gold-muted text-ink hover:border-gold"
              }`}
            >
              {t.label}
            </button>
          ))}
          <AnimatePresence>
            {priceFilter && (
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                onClick={onClearPrice}
                data-testid="price-filter-chip"
                className="h-11 rounded-full px-5 inline-flex items-center gap-2 bg-emerald text-ivory text-[13px] font-semibold"
              >
                {fmtPrice(priceFilter)} altı
                <X size={14} />
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        <motion.div
          layout
          className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
          data-testid="selected-products-grid"
        >
          <AnimatePresence mode="popLayout">
            {items.map((p) => (
              <motion.div
                key={p.id}
                layout
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <ProductCard product={p} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {items.length === 0 && (
          <p className="mt-12 text-center text-sage text-sm" data-testid="catalog-empty-state">
            Bu filtrelere uygun ürün bulunamadı. Filtreyi temizleyip tekrar deneyin.
          </p>
        )}
      </div>
    </section>
  );
};
